import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@repo/tokens";
import { ROUTESWEB } from "@repo/utils";
import "./AskQuestion.css";
import {
  Box,
  VStack,
  Text,
  Icon,
  Modal,
  Radio,
  RadioGroup,
  Header,
} from "@repo/ui";
import {
  DoubtsAiService,
  usePostDc1V2CreateDoubt,
  usePostDc1V2ImageOCR,
} from "@oneapp/data";
import { AnalyticsEvents, SqsEvents, useAnalytics } from "@repo/analytics";
import { useDoubtData } from "../../../lib/DoubtContext";
import { useVisualViewportBottomOverlap } from "../../../hooks/useVisualViewportBottomOverlap";
import { spacingRem } from "../../../lib/spacingRem";
import {
  getDoubtRequestPlatform,
  mapSubjectNameToApiSubject,
} from "../../../lib/subjectSlug";
import { InstructionsModal } from "./uploadFlow/InstructionsModal";
import {
  AdjustBoxModal,
  type ImageUploadCompletePayload,
} from "./uploadFlow/AdjustBoxModal";
import { InappropriateImageModal } from "./uploadFlow/InappropriateImageModal";
import { TrialExpiredModal } from "./uploadFlow/TrialExpiredModal";
import { GetAnswersChat } from "../GetAnswersChat/GetAnswersChat";
import type { GetAnswersChatSession } from "../GetAnswersChat/types";
import type { QuestionItemProps } from "../QuestionItem/types";
import {
  isCreateDoubtQuotaExhaustedError,
  programIdForCreateDoubtBody,
  readCreateDoubtDoubtId,
  readDoubtBuddyMessageId,
  readImageOcrInappropriateFlag,
  readImageOcrUnreadableFlag,
  readImageOcrV2ImageFields,
} from "../../../lib/createDoubtResponse";
import {
  clearAinaReturnAgentForDoubtsChat,
  CREATE_DOUBT_SOURCE_DOUBTS,
  tryReturnToAinaFromDoubtsChat,
} from "../../../lib/ainaDoubtsReturnWeb";
import { useDoubtErrorToast } from "../../../lib/doubtErrorToast";

type PanelUploadPhase = "idle" | "uploading" | "uploaded";

type CropEntrySource = "browse" | "drag";

type FileUploadAreaProps = {
  phase: PanelUploadPhase;
  displayFileName: string;
  uploadProgress: number;
  isDragOver: boolean;
  onPickIntent: () => void;
  onInfoOpen: () => void;
  onBrowseClick: (e: React.MouseEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onCancelPanelUpload: () => void;
  onDiscardUploaded: () => void;
  onContinueUploaded: () => void;
  /** OCR + create doubt after staging Continue (drag path). */
  continueSubmitting?: boolean;
  /** Free doubts + subscription quota exhausted (desktop Figma + Android parity). */
  exhausted?: boolean;
};

const FileUploadArea = ({
  phase,
  displayFileName,
  uploadProgress,
  isDragOver,
  onPickIntent,
  onInfoOpen,
  onBrowseClick,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onCancelPanelUpload,
  onDiscardUploaded,
  onContinueUploaded,
  continueSubmitting = false,
  exhausted = false,
}: FileUploadAreaProps) => {
  const { t } = useTranslation("doubts");
  const { theme } = useTheme();

  const containerClass =
    "file-upload-container" +
    (isDragOver && phase === "idle" && !exhausted
      ? " file-upload-container--drag-active"
      : "") +
    (phase !== "idle" ? " file-upload-container--staging" : "");

  if (phase === "uploading" || phase === "uploaded") {
    const pct = phase === "uploaded" ? 100 : uploadProgress;
    return (
      <div className="ask-upload-staging-wrap">
        <Box
          className={containerClass}
          onDragOver={onDragOver}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <Box className="upload-hero-icon upload-hero-icon--file-status">
            <Icon
              name={
                phase === "uploading" ? "fileUploadIcon" : "uploadedFileIcon"
              }
              width="100%"
              height="auto"
              preserveAspectRatio="xMidYMid meet"
              color="inherit"
            />
          </Box>
          <div
            className="file-upload-progress-card"
            role="status"
            aria-label={
              phase === "uploading"
                ? `${t("uploading_file")}, ${displayFileName}`
                : `${t("file_uploaded")}, ${displayFileName}`
            }
          >
            <div className="file-upload-progress-card__icon-wrap" aria-hidden>
              <Icon name="document" width={22} height={22} color="inherit" />
            </div>
            <div className="file-upload-progress-card__main">
              <span className="file-upload-progress-card__name">
                {displayFileName}
              </span>
              <div className="file-upload-progress-card__row">
                <div className="file-upload-progress-bar-track">
                  <div
                    className="file-upload-progress-bar-fill"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="file-upload-progress-card__pct">{pct}%</span>
              </div>
            </div>
          </div>
        </Box>
        {phase === "uploading" ? (
          <div className="file-upload-drop-footer file-upload-drop-footer--center">
            <button
              type="button"
              className="aq-drop-link-btn"
              onClick={onCancelPanelUpload}
            >
              {t("cancel")}
            </button>
          </div>
        ) : (
          <div className="file-upload-drop-footer">
            <button
              type="button"
              className="aq-drop-link-btn"
              disabled={continueSubmitting}
              onClick={onDiscardUploaded}
            >
              {t("discard")}
            </button>
            <button
              type="button"
              className="aq-drop-primary-btn"
              disabled={continueSubmitting}
              onClick={onContinueUploaded}
            >
              {continueSubmitting ? t("processing") : t("continue")}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (phase === "idle" && exhausted) {
    return (
      <Box
        className={`${containerClass} file-upload-container--exhausted`}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={onPickIntent}
        role="button"
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onPickIntent();
          }
        }}
        aria-disabled="true"
      >
        <Box className="upload-icon-wrapper upload-icon-wrapper--exhausted">
          <Icon
            name="upload"
            width={40}
            height={40}
            color={theme.colors.neutral[50]}
          />
        </Box>
        <div className="doubts-exhausted-text">{t("doubts_exhausted")}</div>
      </Box>
    );
  }

  return (
    <Box
      className={containerClass}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onPickIntent}
    >
      <Box
        className="info-icon-wrapper"
        aria-label={t("file_upload_more_info")}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onInfoOpen();
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onInfoOpen();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <Icon
          name="info"
          width={27}
          height={27}
          color={theme.colors.primary[40]}
        />
      </Box>

      <Box className="upload-icon-wrapper">
        <Icon
          name="upload"
          width={40}
          height={40}
          color={theme.colors.primary[30]}
        />
      </Box>

      <span className="drag-text">{t("drag_and_drop_file")}</span>
      <span className="small-or-text">{t("or")}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onBrowseClick(e);
        }}
        className="browse-text"
      >
        {t("browse_computer")}
      </button>
    </Box>
  );
};

export type AskQuestionPanelProps = {
  onChatSessionClose?: () => void;
  onDoubtsListShouldRefresh?: () => void | Promise<void>;
  /** Sync open chat doubt id for left-panel highlight. */
  onActiveChatDoubtIdChange?: (doubtId: number | null) => void;
  onDoubtResolved?: (doubtId: number) => void;
  onDismissDoubtsFilters?: (options: { resetApplied: boolean }) => void;
  openExistingQuestionChatRef?: React.MutableRefObject<
    ((q: QuestionItemProps) => void) | null
  >;
  layout?: "desktop" | "mweb";
  accessExhausted?: boolean;
  showMwebGreeting?: boolean;
  mwebListSlot?: React.ReactNode;
};

const AskQuestionPanel = ({
  onChatSessionClose,
  onDoubtsListShouldRefresh,
  onActiveChatDoubtIdChange,
  onDoubtResolved,
  onDismissDoubtsFilters,
  openExistingQuestionChatRef,
  layout = "desktop",
  accessExhausted = false,
  showMwebGreeting = false,
  mwebListSlot,
}: AskQuestionPanelProps) => {
  const { t } = useTranslation("doubts");
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const session = useDoubtData();
  const { showSomethingWentWrong } = useDoubtErrorToast();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const uploadRafRef = React.useRef<number | null>(null);
  const pendingDragPayloadRef = React.useRef<ImageUploadCompletePayload | null>(
    null,
  );
  const dragDepthRef = React.useRef(0);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [aqScale, setAqScale] = React.useState(1);

  const [instructionsOpen, setInstructionsOpen] = React.useState(false);
  const [cropOpen, setCropOpen] = React.useState(false);
  const [cropImageUrl, setCropImageUrl] = React.useState<string | null>(null);
  const [cropEntrySource, setCropEntrySource] =
    React.useState<CropEntrySource | null>(null);

  const [panelUploadPhase, setPanelUploadPhase] =
    React.useState<PanelUploadPhase>("idle");
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [droppedDisplayName, setDroppedDisplayName] = React.useState<
    string | null
  >(null);
  const [isFileDragOver, setIsFileDragOver] = React.useState(false);

  const [questionModalOpen, setQuestionModalOpen] = React.useState(false);
  const [subjectModalOpen, setSubjectModalOpen] = React.useState(false);
  const [questionText, setQuestionText] = React.useState("");
  const MIN_QUESTION_LENGTH = 5;
  const isQuestionTextValid = questionText.length >= MIN_QUESTION_LENGTH;
  const [selectedSubjectId, setSelectedSubjectId] = React.useState("");
  const [textSubmitting, setTextSubmitting] = React.useState(false);
  const [textSubmitError, setTextSubmitError] = React.useState<string | null>(
    null,
  );
  const [imagePanelContinuePending, setImagePanelContinuePending] =
    React.useState(false);
  const [imageFlowError, setImageFlowError] = React.useState<string | null>(
    null,
  );
  const [doubtsExhausted, setDoubtsExhausted] = React.useState(false);
  const [trialExpiredModalOpen, setTrialExpiredModalOpen] =
    React.useState(false);
  const [imageIssueModal, setImageIssueModal] = React.useState<
    "inappropriate" | "unreadable" | null
  >(null);

  const [mwebFlowPhase, setMwebFlowPhase] = React.useState<
    "idle" | "askText" | "selectSubject"
  >("idle");

  const mwebFullscreenKbInset = useVisualViewportBottomOverlap(
    layout === "mweb" &&
      (mwebFlowPhase === "askText" || mwebFlowPhase === "selectSubject"),
  );

  const mwebFullscreenFlowStyle = React.useMemo(
    () =>
      ({
        ["--mweb-vv-inset" as string]: `${mwebFullscreenKbInset}px`,
      }) as React.CSSProperties,
    [mwebFullscreenKbInset],
  );

  const [chatSession, setChatSession] =
    React.useState<GetAnswersChatSession | null>(null);
  const chatRevokeUrlRef = React.useRef<string | null>(null);

  const resolvedChatSession = chatSession;

  React.useEffect(() => {
    const id = chatSession?.doubtId;
    onActiveChatDoubtIdChange?.(id != null && id > 0 ? id : null);
  }, [chatSession, onActiveChatDoubtIdChange]);

  React.useLayoutEffect(() => {
    if (!openExistingQuestionChatRef) return;
    openExistingQuestionChatRef.current = (q: QuestionItemProps) => {
      if (q.doubtId == null || q.doubtId <= 0) return;

      clearAinaReturnAgentForDoubtsChat();

      track(AnalyticsEvents.DOUBTS_THREAD_OPENED_FROM_LIST, 1, {
        doubt_id: q.doubtId,
        preview_kind: q.image ? "image" : "text",
      });

      const subjectId =
        q.subjectId != null && q.subjectId > 0 ? q.subjectId : 1;

      const displayName = q.subject ?? "";
      const textTrim = (q.text ?? "").trim();
      const questionText =
        textTrim ||
        (q.image
          ? displayName || t("question_image_a11y")
          : t("no_text_available"));

      const qbFromItem = q.qbId != null && q.qbId > 0 ? q.qbId : undefined;
      const qbFromSession =
        session.qbId != null && session.qbId > 0 ? session.qbId : undefined;
      const qbId = qbFromItem ?? qbFromSession;

      const base = {
        doubtId: q.doubtId,
        subjectId,
        questionText,
        subjectDisplayName: displayName,
        subjectForSolution: mapSubjectNameToApiSubject(displayName),
        doubtStatus: q.status,
        ...(qbId != null ? { qbId } : {}),
        ...(q.doubtBuddyMessageId != null
          ? { doubtBuddyMessageId: q.doubtBuddyMessageId }
          : {}),
      };

      chatRevokeUrlRef.current = null;
      setMwebFlowPhase("idle");
      setQuestionModalOpen(false);
      setSubjectModalOpen(false);

      if (q.image) {
        setChatSession({
          kind: "image",
          ...base,
          imageUrl: q.image,
        });
      } else {
        setChatSession({
          kind: "text",
          ...base,
        });
      }
    };
    return () => {
      openExistingQuestionChatRef.current = null;
    };
  }, [openExistingQuestionChatRef, session.qbId, t, track]);

  const effectiveExhausted = doubtsExhausted || accessExhausted;

  const subjectQueryEnabled =
    Boolean(session.gradeId && session.tenantId) &&
    (subjectModalOpen ||
      cropOpen ||
      (layout === "mweb" && mwebFlowPhase === "selectSubject"));

  const { data: subjectsRaw, ...subjectsQuery } =
    DoubtsAiService.useGetDc1MetaSubjectsGradeMapping(
      {
        gradeId: session.gradeId ?? 0,
        tenantId: session.tenantId ?? "",
      },
      {
        query: { enabled: subjectQueryEnabled },
        request: { clientName: "doubtsAi" },
      },
    );

  const apiSubjects = React.useMemo(() => {
    const raw = subjectsRaw as unknown;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item: unknown) => {
        const row = item as {
          subjectId?: number;
          subject?: { name?: string };
        };
        return {
          subjectId: row.subjectId as number,
          name: row.subject?.name ?? "",
        };
      })
      .filter(
        (s) =>
          s.subjectId != null &&
          Number.isFinite(s.subjectId) &&
          s.name.length > 0,
      );
  }, [subjectsRaw]);

  const { mutateAsync: createDoubtAsync } = usePostDc1V2CreateDoubt({
    request: { clientName: "doubtsDCAPI" },
  });

  const { mutateAsync: runImageOcrAsync } = usePostDc1V2ImageOCR({
    request: { clientName: "doubtsDCAPI" },
  });

  const runImageDoubtFlow = React.useCallback(
    async (payload: ImageUploadCompletePayload) => {
      if (effectiveExhausted) {
        URL.revokeObjectURL(payload.previewObjectUrl);
        return;
      }
      if (
        !session.tenantId ||
        session.gradeId == null ||
        session.uamId == null ||
        programIdForCreateDoubtBody(session.programId) == null
      ) {
        setImageFlowError(t("session_missing_subjects"));
        URL.revokeObjectURL(payload.previewObjectUrl);
        return;
      }
      setImageFlowError(null);
      try {
        const ocrRes = await runImageOcrAsync({
          data: { file: payload.blob },
        });
        const issue: "inappropriate" | "unreadable" | null =
          readImageOcrInappropriateFlag(ocrRes)
            ? "inappropriate"
            : readImageOcrUnreadableFlag(ocrRes)
              ? "unreadable"
              : null;
        if (issue != null) {
          setImageIssueModal(issue);
          setImageFlowError(null);
          pendingDragPayloadRef.current = null;
          setPanelUploadPhase("idle");
          setUploadProgress(0);
          setDroppedDisplayName(null);
          dragDepthRef.current = 0;
          setIsFileDragOver(false);
          URL.revokeObjectURL(payload.previewObjectUrl);
          return;
        }
        const qRaw = (ocrRes as { data?: { question?: string } })?.data
          ?.question;
        const q = typeof qRaw === "string" ? qRaw.trim() : "";
        const doubtText = q || t("ocr_no_text");

        const { imageId, imagePath } = readImageOcrV2ImageFields(ocrRes);
        const createBody: Record<string, unknown> = {
          doubtText,
          freeDoubt: true,
          subjectId: payload.subjectId,
          tenantId: session.tenantId,
          userGradeId: session.gradeId,
          userId: session.uamId,
          platform: getDoubtRequestPlatform(),
          source: CREATE_DOUBT_SOURCE_DOUBTS,
          programId: programIdForCreateDoubtBody(session.programId),
        };
        if (imageId != null) {
          createBody.imageId = imageId;
        }
        if (imagePath != null) {
          createBody.imagePath = imagePath;
        }

        const createRes = await createDoubtAsync({ data: createBody });
        const doubtId = readCreateDoubtDoubtId(createRes);
        const doubtBuddyMessageId = readDoubtBuddyMessageId(createRes);
        if (createRes.success && createRes.doubtId) {
          SqsEvents.actDoubtActivity({
            subject: payload.subjectName,
          });
        }
        track(AnalyticsEvents.DOUBTS_QUESTION_CREATED, 1, {
          submission_type: "image",
        });
        chatRevokeUrlRef.current = payload.previewObjectUrl;
        onDismissDoubtsFilters?.({ resetApplied: true });
        const refreshResult = onDoubtsListShouldRefresh?.();
        if (
          refreshResult &&
          typeof (refreshResult as Promise<void>).then === "function"
        ) {
          await refreshResult;
        }
        setChatSession({
          kind: "image",
          doubtId,
          subjectId: payload.subjectId,
          questionText: doubtText,
          subjectDisplayName: payload.subjectName,
          subjectForSolution: mapSubjectNameToApiSubject(payload.subjectName),
          imageUrl: payload.previewObjectUrl,
          ...(doubtBuddyMessageId != null ? { doubtBuddyMessageId } : {}),
          ...(session.qbId != null && session.qbId > 0
            ? { qbId: session.qbId }
            : {}),
        });
      } catch (e) {
        if (readImageOcrInappropriateFlag(e)) {
          setImageIssueModal("inappropriate");
          setImageFlowError(null);
        } else if (isCreateDoubtQuotaExhaustedError(e)) {
          setDoubtsExhausted(true);
          setImageFlowError(null);
        } else {
          showSomethingWentWrong(e);
          setImageFlowError(null);
        }
        if (uploadRafRef.current != null) {
          cancelAnimationFrame(uploadRafRef.current);
          uploadRafRef.current = null;
        }
        pendingDragPayloadRef.current = null;
        setPanelUploadPhase("idle");
        setUploadProgress(0);
        setDroppedDisplayName(null);
        dragDepthRef.current = 0;
        setIsFileDragOver(false);
        URL.revokeObjectURL(payload.previewObjectUrl);
      }
    },
    [
      createDoubtAsync,
      effectiveExhausted,
      runImageOcrAsync,
      session.gradeId,
      session.programId,
      session.tenantId,
      session.uamId,
      showSomethingWentWrong,
      t,
      track,
      onDismissDoubtsFilters,
      onDoubtsListShouldRefresh,
    ],
  );

  const revokeCropUrl = React.useCallback(() => {
    setCropImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const stopFakeUpload = React.useCallback(() => {
    if (uploadRafRef.current != null) {
      cancelAnimationFrame(uploadRafRef.current);
      uploadRafRef.current = null;
    }
  }, []);

  const resetDragUploadPanel = React.useCallback(() => {
    stopFakeUpload();
    const p = pendingDragPayloadRef.current;
    if (p) {
      URL.revokeObjectURL(p.previewObjectUrl);
      pendingDragPayloadRef.current = null;
    }
    setPanelUploadPhase("idle");
    setUploadProgress(0);
    setDroppedDisplayName(null);
  }, [stopFakeUpload]);

  const handleCloseChat = React.useCallback(
    (options?: { navigateToAina?: boolean }) => {
      if (chatRevokeUrlRef.current) {
        URL.revokeObjectURL(chatRevokeUrlRef.current);
        chatRevokeUrlRef.current = null;
      }
      setChatSession(null);
      setImageFlowError(null);
      resetDragUploadPanel();
      if (
        options?.navigateToAina === true &&
        tryReturnToAinaFromDoubtsChat(navigate)
      ) {
        onChatSessionClose?.();
        return;
      }
      onChatSessionClose?.();
    },
    [resetDragUploadPanel, onChatSessionClose, navigate],
  );

  React.useEffect(() => () => stopFakeUpload(), [stopFakeUpload]);

  React.useLayoutEffect(() => {
    if (layout !== "desktop") return;
    if (resolvedChatSession) return;
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;

    const updateScale = () => {
      const wrapperHeight = wrapper.clientHeight;
      const contentHeight = content.scrollHeight;
      if (wrapperHeight <= 0 || contentHeight <= 0) return;
      const next = Math.min(1, wrapperHeight / contentHeight);
      setAqScale((prev) => (Math.abs(next - prev) > 0.002 ? next : prev));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(wrapper);
    observer.observe(content);
    return () => observer.disconnect();
  }, [layout, resolvedChatSession]);

  const openCropWithFile = React.useCallback(
    (file: File, source: CropEntrySource) => {
      if (effectiveExhausted) {
        setTrialExpiredModalOpen(true);
        return;
      }
      onDismissDoubtsFilters?.({ resetApplied: false });
      revokeCropUrl();
      setCropEntrySource(source);
      if (source === "drag") {
        setDroppedDisplayName(file.name);
      } else {
        setDroppedDisplayName(null);
      }
      const url = URL.createObjectURL(file);
      setCropImageUrl(url);
      setCropOpen(true);
    },
    [effectiveExhausted, onDismissDoubtsFilters, revokeCropUrl],
  );

  const cancelCropFlow = React.useCallback(() => {
    stopFakeUpload();
    const p = pendingDragPayloadRef.current;
    if (p) {
      URL.revokeObjectURL(p.previewObjectUrl);
      pendingDragPayloadRef.current = null;
    }
    revokeCropUrl();
    setCropOpen(false);
    setCropEntrySource(null);
    setDroppedDisplayName(null);
    setPanelUploadPhase("idle");
    setUploadProgress(0);
  }, [revokeCropUrl, stopFakeUpload]);

  const closeInstructions = React.useCallback(() => {
    setInstructionsOpen(false);
  }, []);

  const handlePickIntent = React.useCallback(() => {
    if (effectiveExhausted) {
      setTrialExpiredModalOpen(true);
      return;
    }
    if (panelUploadPhase !== "idle") return;
    onDismissDoubtsFilters?.({ resetApplied: false });
    fileInputRef.current?.click();
  }, [effectiveExhausted, onDismissDoubtsFilters, panelUploadPhase]);

  const handleImageIssueReupload = React.useCallback(() => {
    setImageIssueModal(null);
    if (effectiveExhausted) {
      setTrialExpiredModalOpen(true);
      return;
    }
    queueMicrotask(() => fileInputRef.current?.click());
  }, [effectiveExhausted]);

  const handleInfoOpen = React.useCallback(() => {
    if (effectiveExhausted) {
      setTrialExpiredModalOpen(true);
      return;
    }
    if (panelUploadPhase !== "idle") return;
    setInstructionsOpen(true);
  }, [effectiveExhausted, panelUploadPhase]);

  const handleBrowseClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (effectiveExhausted) {
        setTrialExpiredModalOpen(true);
        return;
      }
      if (panelUploadPhase !== "idle") return;
      fileInputRef.current?.click();
    },
    [effectiveExhausted, panelUploadPhase],
  );

  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (panelUploadPhase !== "idle") return;
      dragDepthRef.current += 1;
      if (dragDepthRef.current === 1) setIsFileDragOver(true);
    },
    [panelUploadPhase],
  );

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsFileDragOver(false);
    }
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current = 0;
      setIsFileDragOver(false);
      if (effectiveExhausted) {
        setTrialExpiredModalOpen(true);
        return;
      }
      if (panelUploadPhase !== "idle") return;
      const f = Array.from(e.dataTransfer.files).find((file) =>
        file.type.startsWith("image/"),
      );
      if (f) {
        openCropWithFile(f, "drag");
      }
    },
    [effectiveExhausted, openCropWithFile, panelUploadPhase],
  );

  const handleFileInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (effectiveExhausted) {
        e.target.value = "";
        setTrialExpiredModalOpen(true);
        return;
      }
      const f = e.target.files?.[0];
      e.target.value = "";
      if (f?.type.startsWith("image/")) {
        openCropWithFile(f, "browse");
      }
    },
    [effectiveExhausted, openCropWithFile],
  );

  const startFakeUploadProgress = React.useCallback(() => {
    stopFakeUpload();
    const start = performance.now();
    const duration = 1600;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      setUploadProgress(Math.round(t * 100));
      if (t < 1) {
        uploadRafRef.current = requestAnimationFrame(tick);
      } else {
        uploadRafRef.current = null;
        setPanelUploadPhase("uploaded");
      }
    };
    uploadRafRef.current = requestAnimationFrame(tick);
  }, [stopFakeUpload]);

  const handleBrowseImageComplete = React.useCallback(
    async (payload: ImageUploadCompletePayload) => {
      if (cropEntrySource === "drag") {
        pendingDragPayloadRef.current = payload;
        setCropOpen(false);
        revokeCropUrl();
        setCropEntrySource(null);
        setPanelUploadPhase("uploading");
        setUploadProgress(0);
        startFakeUploadProgress();
        return;
      }
      await runImageDoubtFlow(payload);
      revokeCropUrl();
      setCropOpen(false);
      setCropEntrySource(null);
    },
    [
      cropEntrySource,
      revokeCropUrl,
      runImageDoubtFlow,
      startFakeUploadProgress,
    ],
  );

  const handleCancelPanelUpload = React.useCallback(() => {
    resetDragUploadPanel();
  }, [resetDragUploadPanel]);

  const handleDiscardUploaded = React.useCallback(() => {
    resetDragUploadPanel();
  }, [resetDragUploadPanel]);

  const handleContinueUploaded = React.useCallback(async () => {
    const p = pendingDragPayloadRef.current;
    pendingDragPayloadRef.current = null;
    if (p) {
      setImagePanelContinuePending(true);
      try {
        await runImageDoubtFlow(p);
      } finally {
        setImagePanelContinuePending(false);
      }
    }
    setPanelUploadPhase("idle");
    setUploadProgress(0);
    setDroppedDisplayName(null);
  }, [runImageDoubtFlow]);

  const openQuestionFlow = React.useCallback(() => {
    if (effectiveExhausted) {
      setTrialExpiredModalOpen(true);
      return;
    }
    onDismissDoubtsFilters?.({ resetApplied: false });
    if (layout === "mweb") {
      setMwebFlowPhase("askText");
    } else {
      setQuestionModalOpen(true);
    }
  }, [effectiveExhausted, layout, onDismissDoubtsFilters]);

  const handleGoToSubscriptions = React.useCallback(() => {
    setTrialExpiredModalOpen(false);
    navigate(`/${ROUTESWEB.APP.SUBSCRIPTIONS}`);
  }, [navigate]);

  const closeQuestionModal = React.useCallback(() => {
    setQuestionModalOpen(false);
    setQuestionText("");
  }, []);

  const closeMwebAskFlow = React.useCallback(() => {
    onDismissDoubtsFilters?.({ resetApplied: false });
    setMwebFlowPhase("idle");
    setQuestionText("");
    setSelectedSubjectId("");
    setTextSubmitError(null);
  }, [onDismissDoubtsFilters]);

  const goToSubjectStep = React.useCallback(() => {
    if (questionText.length < MIN_QUESTION_LENGTH) return;
    setSelectedSubjectId("");
    setTextSubmitError(null);
    if (layout === "mweb") {
      setMwebFlowPhase("selectSubject");
    } else {
      setQuestionModalOpen(false);
      setSubjectModalOpen(true);
    }
  }, [layout, questionText]);

  const leaveSubjectModal = React.useCallback(() => {
    setTextSubmitError(null);
    if (layout === "mweb") {
      setMwebFlowPhase("askText");
    } else {
      setSubjectModalOpen(false);
      setQuestionModalOpen(true);
    }
  }, [layout]);

  const finishSubjectStep = React.useCallback(async () => {
    if (effectiveExhausted) return;
    if (!selectedSubjectId) return;
    if (
      !session.tenantId ||
      session.gradeId == null ||
      session.uamId == null ||
      programIdForCreateDoubtBody(session.programId) == null
    ) {
      setTextSubmitError(t("session_missing_subjects"));
      return;
    }
    setTextSubmitting(true);
    setTextSubmitError(null);
    try {
      const createRes = await createDoubtAsync({
        data: {
          doubtText: questionText.trim(),
          freeDoubt: true,
          subjectId: Number(selectedSubjectId),
          tenantId: session.tenantId,
          userGradeId: session.gradeId,
          userId: session.uamId,
          platform: getDoubtRequestPlatform(),
          source: CREATE_DOUBT_SOURCE_DOUBTS,
          programId: programIdForCreateDoubtBody(session.programId),
        } as Record<string, unknown>,
      });
      const doubtId = readCreateDoubtDoubtId(createRes);
      const doubtBuddyMessageId = readDoubtBuddyMessageId(createRes);
      if (doubtId) {
        SqsEvents.actDoubtActivity({
          subject:
            apiSubjects.find((s) => String(s.subjectId) === selectedSubjectId)
              ?.name ?? "",
        });
      }
      track(AnalyticsEvents.DOUBTS_QUESTION_CREATED, 1, {
        submission_type: "text",
      });
      const subj = apiSubjects.find(
        (s) => String(s.subjectId) === selectedSubjectId,
      );
      chatRevokeUrlRef.current = null;
      onDismissDoubtsFilters?.({ resetApplied: true });
      const refreshResult = onDoubtsListShouldRefresh?.();
      if (
        refreshResult &&
        typeof (refreshResult as Promise<void>).then === "function"
      ) {
        await refreshResult;
      }
      setChatSession({
        kind: "text",
        doubtId,
        subjectId: Number(selectedSubjectId),
        questionText: questionText.trim(),
        subjectDisplayName: subj?.name ?? "",
        subjectForSolution: mapSubjectNameToApiSubject(subj?.name ?? ""),
        ...(doubtBuddyMessageId != null ? { doubtBuddyMessageId } : {}),
        ...(session.qbId != null && session.qbId > 0
          ? { qbId: session.qbId }
          : {}),
      });
      revokeCropUrl();
      setCropOpen(false);
      setSubjectModalOpen(false);
      setQuestionModalOpen(false);
      setQuestionText("");
      setSelectedSubjectId("");
      setMwebFlowPhase("idle");
    } catch (e) {
      if (isCreateDoubtQuotaExhaustedError(e)) {
        setDoubtsExhausted(true);
        setTextSubmitError(null);
        setSubjectModalOpen(false);
        setQuestionModalOpen(false);
        setQuestionText("");
        setSelectedSubjectId("");
        setMwebFlowPhase("idle");
      } else {
        showSomethingWentWrong(e);
        setTextSubmitError(null);
        setSelectedSubjectId("");
        if (layout === "mweb") {
          setMwebFlowPhase("askText");
        } else {
          setSubjectModalOpen(false);
          setQuestionModalOpen(true);
        }
      }
    } finally {
      setTextSubmitting(false);
    }
  }, [
    apiSubjects,
    createDoubtAsync,
    effectiveExhausted,
    layout,
    questionText,
    revokeCropUrl,
    selectedSubjectId,
    session.gradeId,
    session.programId,
    session.tenantId,
    session.uamId,
    showSomethingWentWrong,
    t,
    track,
    onDismissDoubtsFilters,
    onDoubtsListShouldRefresh,
  ]);

  const questionHeader = (
    <Box className="modal-header">
      <div className="aq-modal-title">{t("ask_a_question")}</div>
      <button
        type="button"
        className="close-icon-wrapper"
        onClick={closeQuestionModal}
        aria-label={t("cancel")}
      >
        <Icon
          name="close"
          width={24}
          height={24}
          color={theme.colors.primary[40]}
        />
      </button>
    </Box>
  );

  const subjectHeader = (
    <Box className="modal-header">
      <div className="aq-modal-title">{t("select_subject_modal_title")}</div>
      <button
        type="button"
        className="close-icon-wrapper"
        onClick={leaveSubjectModal}
        aria-label={t("cancel")}
      >
        <Icon
          name="close"
          width={24}
          height={24}
          color={theme.colors.primary[40]}
        />
      </button>
    </Box>
  );

  const questionBody = (
    <div className="aq-modal-body aq-modal-body--question">
      <textarea
        className="aq-modal-textarea"
        placeholder={t("type_here")}
        rows={6}
        value={questionText}
        onChange={(e) => setQuestionText(e.target.value)}
      />
      <div className="aq-btn-container">
        <button
          type="button"
          className="clear-btn"
          onClick={closeQuestionModal}
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          className="apply-btn next-btn"
          disabled={!isQuestionTextValid}
          onClick={goToSubjectStep}
        >
          {t("next")}
        </button>
      </div>
    </div>
  );

  const subjectBody = (
    <div className="aq-modal-body aq-modal-body--subject">
      {textSubmitError ? (
        <Text as="p" className="aq-subject-section-label" role="alert">
          {textSubmitError}
        </Text>
      ) : null}
      {!session.gradeId || !session.tenantId ? (
        <Text as="p" className="aq-subject-section-label">
          {t("session_missing_subjects")}
        </Text>
      ) : subjectsQuery.isLoading ? (
        <Text as="p" className="aq-subject-section-label">
          {t("subjects_loading")}
        </Text>
      ) : subjectsQuery.isError ? (
        <div className="aq-btn-container aq-btn-container--subject-footer">
          <Text as="p" className="aq-subject-section-label">
            {t("subjects_load_error")}
          </Text>
          <button
            type="button"
            className="apply-btn"
            onClick={() => void subjectsQuery.refetch()}
          >
            {t("subjects_retry")}
          </button>
        </div>
      ) : (
        <div className="aq-subject-radio-list">
          <RadioGroup
            name="doubts-ask-subject"
            value={selectedSubjectId}
            onChange={setSelectedSubjectId}
            gap="xl"
          >
            {apiSubjects.map((row) => (
              <Radio
                key={row.subjectId}
                value={String(row.subjectId)}
                label={row.name}
                size="lg"
              />
            ))}
          </RadioGroup>
        </div>
      )}
      <div className="aq-btn-container aq-btn-container--subject-footer">
        <button type="button" className="clear-btn" onClick={leaveSubjectModal}>
          {t("cancel")}
        </button>
        <button
          type="button"
          className="apply-btn"
          disabled={
            textSubmitting ||
            !selectedSubjectId ||
            subjectsQuery.isLoading ||
            subjectsQuery.isError ||
            !session.gradeId ||
            !session.tenantId
          }
          onClick={() => void finishSubjectStep()}
        >
          {textSubmitting ? t("processing") : t("apply")}
        </button>
      </div>
    </div>
  );

  const uploadAreaFileName = droppedDisplayName ?? "image";
  const showTextQuestionPath =
    panelUploadPhase === "idle" &&
    (layout === "desktop" || mwebFlowPhase === "idle");

  const fileInputEl = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      className="aq-hidden-file-input"
      aria-hidden
      tabIndex={-1}
      onChange={handleFileInputChange}
    />
  );

  const trialAndInstructions = (
    <>
      <TrialExpiredModal
        isOpen={trialExpiredModalOpen}
        onClose={() => setTrialExpiredModalOpen(false)}
        onGoToSubscriptions={handleGoToSubscriptions}
      />
      <InappropriateImageModal
        isOpen={imageIssueModal != null}
        variant={imageIssueModal ?? "inappropriate"}
        onReupload={handleImageIssueReupload}
      />
      <InstructionsModal
        isOpen={instructionsOpen}
        onClose={closeInstructions}
      />
    </>
  );

  const cropModalEl =
    cropOpen && cropImageUrl ? (
      <AdjustBoxModal
        isOpen={cropOpen}
        imageUrl={cropImageUrl}
        onCancel={cancelCropFlow}
        onComplete={(p) => handleBrowseImageComplete(p)}
        subjects={apiSubjects}
        subjectsLoading={subjectsQuery.isLoading}
        subjectsError={subjectsQuery.isError}
        sessionSubjectsReady={Boolean(session.gradeId && session.tenantId)}
        onRetrySubjects={() => void subjectsQuery.refetch()}
      />
    ) : null;

  if (resolvedChatSession) {
    return (
      <VStack
        className="ask-question-wrapper ask-question-wrapper--chat-active"
        style={{ width: "100%" }}
      >
        <GetAnswersChat
          session={resolvedChatSession}
          userFirstName={session.firstName ?? ""}
          onClose={handleCloseChat}
          onDoubtResolved={onDoubtResolved}
          layout={layout === "mweb" ? "fullscreen" : "embedded"}
        />
      </VStack>
    );
  }

  const mwebAskTextScreen =
    layout === "mweb" && mwebFlowPhase === "askText" ? (
      <div className="mweb-fullscreen-flow" style={mwebFullscreenFlowStyle}>
        <Header
          title={t("ask_a_question")}
          onBackPress={closeMwebAskFlow}
          leftVariant="back"
          backIcon="circleBackBtn"
          backIconShadow
          backIconWidth={48}
          backIconHeight={48}
          showRight={false}
          containerStyles={{
            marginTop: spacingRem(theme.spacing.md),
            marginBottom: spacingRem(theme.spacing.md),
          }}
          sectionLeftStyles={{ marginLeft: spacingRem(theme.spacing.sm) }}
          titleStyles={{
            ...theme.typography.title.medium.emphasized,
            textAlign: "center",
          }}
        />
        <div className="mweb-fullscreen-flow-body mweb-fullscreen-flow-body--ask-text">
          <textarea
            className="mweb-fullscreen-textarea"
            placeholder={t("type_here")}
            rows={4}
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
          />
        </div>
        <div className="mweb-fullscreen-flow-footer">
          <button
            type="button"
            className="mweb-primary-cta"
            disabled={!isQuestionTextValid}
            onClick={goToSubjectStep}
          >
            {t("next")}
          </button>
        </div>
      </div>
    ) : null;

  const mwebSubjectScreen =
    layout === "mweb" && mwebFlowPhase === "selectSubject" ? (
      <div className="mweb-fullscreen-flow" style={mwebFullscreenFlowStyle}>
        <Header
          title={t("select_subject_modal_title")}
          onBackPress={leaveSubjectModal}
          leftVariant="back"
          backIcon="circleBackBtn"
          backIconShadow
          backIconWidth={48}
          backIconHeight={48}
          showRight={false}
          containerStyles={{
            marginTop: spacingRem(theme.spacing.md),
            marginBottom: spacingRem(theme.spacing.md),
          }}
          sectionLeftStyles={{ marginLeft: spacingRem(theme.spacing.sm) }}
          titleStyles={{
            ...theme.typography.title.medium.emphasized,
            textAlign: "center",
          }}
        />
        <div className="mweb-fullscreen-flow-body mweb-fullscreen-flow-body--subject">
          {textSubmitError ? (
            <Text as="p" className="aq-subject-section-label" role="alert">
              {textSubmitError}
            </Text>
          ) : null}
          {!session.gradeId || !session.tenantId ? (
            <Text as="p" className="aq-subject-section-label">
              {t("session_missing_subjects")}
            </Text>
          ) : subjectsQuery.isLoading ? (
            <Text as="p" className="aq-subject-section-label">
              {t("subjects_loading")}
            </Text>
          ) : subjectsQuery.isError ? (
            <div className="aq-btn-container aq-btn-container--subject-footer">
              <Text as="p" className="aq-subject-section-label">
                {t("subjects_load_error")}
              </Text>
              <button
                type="button"
                className="apply-btn"
                onClick={() => void subjectsQuery.refetch()}
              >
                {t("subjects_retry")}
              </button>
            </div>
          ) : (
            <div className="aq-subject-radio-list">
              <RadioGroup
                name="doubts-ask-subject-mweb"
                value={selectedSubjectId}
                onChange={setSelectedSubjectId}
                gap="xl"
              >
                {apiSubjects.map((row) => (
                  <Radio
                    key={row.subjectId}
                    value={String(row.subjectId)}
                    label={row.name}
                    size="md"
                  />
                ))}
              </RadioGroup>
            </div>
          )}
        </div>
        <div className="mweb-subject-footer">
          <div className="mweb-subject-footer-actions">
            <button
              type="button"
              className="mweb-secondary-cta"
              onClick={() => void leaveSubjectModal()}
            >
              {t("back")}
            </button>
            <button
              type="button"
              className="mweb-primary-cta"
              disabled={
                textSubmitting ||
                !selectedSubjectId ||
                subjectsQuery.isLoading ||
                subjectsQuery.isError ||
                !session.gradeId ||
                !session.tenantId
              }
              onClick={() => void finishSubjectStep()}
            >
              {textSubmitting ? t("processing") : t("ask")}
            </button>
          </div>
        </div>
      </div>
    ) : null;

  if (layout === "mweb") {
    return (
      <>
        <div className="doubts-mweb-ask-root">
          {showMwebGreeting ? (
            <div className="doubts-mweb-greeting">
              <p className="doubts-mweb-greeting-title">
                {t("mweb_welcome_hi", { name: session.firstName ?? "" })}
              </p>
              <p className="doubts-mweb-greeting-body">
                {t("mweb_welcome_body")}
              </p>
            </div>
          ) : null}
          <div className="doubts-mweb-main-container">
            {fileInputEl}
            {imageFlowError && !effectiveExhausted ? (
              <Text as="p" className="aq-flow-error" role="alert">
                {imageFlowError}
              </Text>
            ) : null}
            {mwebFlowPhase === "idle" ? (
              <>
                <h2 className="doubts-mweb-ask-title">
                  {t("ask_a_question_title")}
                </h2>
                {panelUploadPhase === "idle" ? (
                  <button
                    type="button"
                    className={
                      "doubts-mweb-upload-card" +
                      (effectiveExhausted
                        ? " doubts-mweb-upload-card--exhausted"
                        : "")
                    }
                    onClick={handlePickIntent}
                    aria-disabled={effectiveExhausted}
                  >
                    <Icon
                      name={
                        effectiveExhausted ? "doubtExhaustLockIcon" : "Camera"
                      }
                      width={28}
                      height={28}
                      color={
                        effectiveExhausted
                          ? theme.colors.neutral[100]
                          : theme.colors.primary[40]
                      }
                    />
                    <span className="doubts-mweb-upload-label">
                      {effectiveExhausted
                        ? t("doubts_exhausted")
                        : t("upload_photo")}
                    </span>
                  </button>
                ) : (
                  <FileUploadArea
                    phase={panelUploadPhase}
                    displayFileName={uploadAreaFileName}
                    uploadProgress={uploadProgress}
                    isDragOver={isFileDragOver}
                    onPickIntent={handlePickIntent}
                    onInfoOpen={handleInfoOpen}
                    onBrowseClick={handleBrowseClick}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onCancelPanelUpload={handleCancelPanelUpload}
                    onDiscardUploaded={handleDiscardUploaded}
                    onContinueUploaded={handleContinueUploaded}
                    continueSubmitting={imagePanelContinuePending}
                    exhausted={effectiveExhausted}
                  />
                )}
                {panelUploadPhase === "idle" ? (
                  <>
                    <span className="doubts-mweb-or">{t("or")}</span>
                    <button
                      type="button"
                      className="doubts-mweb-type-pill"
                      onClick={openQuestionFlow}
                    >
                      {effectiveExhausted ? (
                        <Icon
                          name="doubtExhaustLockAskIcon"
                          width={40}
                          height={40}
                          color={theme.colors.neutral[80]}
                        />
                      ) : null}
                      {t("type_here_placeholder")}
                    </button>
                  </>
                ) : null}
              </>
            ) : null}
          </div>
          {mwebListSlot}
        </div>
        {mwebAskTextScreen}
        {mwebSubjectScreen}
        {trialAndInstructions}
        {cropModalEl}
      </>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="ask-question-wrapper"
      style={{ width: "100%" }}
    >
      <div
        ref={contentRef}
        className="ask-question-scaler"
        style={{
          transform: `scale(${aqScale})`,
          transformOrigin: "top left",
          width: `${(1 / aqScale) * 100}%`,
        }}
      >
        {imageFlowError && !effectiveExhausted ? (
          <Text as="p" className="aq-flow-error" role="alert">
            {imageFlowError}
          </Text>
        ) : null}
        {fileInputEl}

        <Box
          as="h3"
          style={{
            ...theme.typography.title.large.regular,
            fontWeight: "500",
          }}
        >
          {t("ask_a_question_title")}
        </Box>

        {showMwebGreeting ? (
          <div className="aq-desktop-welcome">
            <p className="aq-desktop-welcome-title">
              {t("mweb_welcome_hi", { name: session.firstName ?? "" })}
            </p>
            <p className="aq-desktop-welcome-body">
              {t("desktop_welcome_body")}
            </p>
          </div>
        ) : null}

        <FileUploadArea
          phase={panelUploadPhase}
          displayFileName={uploadAreaFileName}
          uploadProgress={uploadProgress}
          isDragOver={isFileDragOver}
          onPickIntent={handlePickIntent}
          onInfoOpen={handleInfoOpen}
          onBrowseClick={handleBrowseClick}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onCancelPanelUpload={handleCancelPanelUpload}
          onDiscardUploaded={handleDiscardUploaded}
          onContinueUploaded={handleContinueUploaded}
          continueSubmitting={imagePanelContinuePending}
          exhausted={effectiveExhausted}
        />

        {showTextQuestionPath ? (
          <>
            <Box className="divider-wrapper">
              <Text as="span" className="divider-text">
                {t("or")}
              </Text>
            </Box>

            <div
              className="question-input-container"
              onClick={openQuestionFlow}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openQuestionFlow();
                }
              }}
              role="button"
              tabIndex={0}
              aria-disabled={effectiveExhausted}
            >
              {t("ask_a_question_pill")}
            </div>
          </>
        ) : null}

        {trialAndInstructions}

        {cropModalEl}

        <Modal
          isModalOpen={questionModalOpen}
          onClose={closeQuestionModal}
          showFooter={false}
          width={640}
          height={431}
          customHeader={questionHeader}
          body={questionBody}
          alignItems="center"
        />

        <Modal
          isModalOpen={subjectModalOpen}
          onClose={leaveSubjectModal}
          showFooter={false}
          width="min(36rem, 94vw)"
          height="auto"
          modalClassName="aq-adjust-modal--subject"
          bodyClassName="aq-adjust-modal-body--subject"
          customHeader={subjectHeader}
          body={subjectBody}
          alignItems="center"
        />
      </div>
    </div>
  );
};

export default AskQuestionPanel;
